import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import type { Bot, Routine } from "@rakazo/contracts";
import { Kbd } from "@rakazo/ui-web";
import { Clock, Inbox, LayoutGrid, Plus, Search } from "lucide-react";
import type { ReactNode } from "react";
import type { SidebarTab } from "../../lib/layout";
import { reviewInboxEnabled } from "../../lib/review-inbox-flag";
import { useReviewCount } from "../../lib/review-inbox-query";

/** How many routines the chat tab lists before pointing to the routines tab. */
export const SIDEBAR_ROUTINE_PREVIEW_COUNT = 5;

function isApplePlatform() {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent);
}

/** Short label for a routine's next run: time today, "tomorrow", weekday within a week, else date. */
export function formatNextRun(iso: string | null, now = new Date()): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const locale = i18n.locale || "en";
  const time = date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  const startOfDay = (value: Date) =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const days = Math.round((startOfDay(date) - startOfDay(now)) / 86_400_000);
  if (days <= 0) return time;
  if (days === 1) return t`tomorrow ${time}`;
  if (days < 7) return `${date.toLocaleDateString(locale, { weekday: "short" })} ${time}`;
  return date.toLocaleDateString(locale, { month: "short", day: "numeric" });
}

/** Tab strip at the top of the sidebar: Chat, Routines, Computer. */
export function SidebarTabs({
  tab,
  onTabChange,
}: {
  tab: SidebarTab;
  onTabChange: (tab: SidebarTab) => void;
}) {
  const { t } = useLingui();
  const tabs: { id: SidebarTab; label: string }[] = [
    { id: "chat", label: t`Chat` },
    { id: "routines", label: t`Routines` },
    { id: "computer", label: t({ message: "Computer", context: "sidebar tab" }) },
  ];
  return (
    <div
      role="tablist"
      aria-label={t`Sidebar view`}
      data-testid="sidebar-tabs"
      className="app-no-drag flex min-w-0 flex-1 rounded-lg bg-sidebar-accent/70 p-0.5"
      onKeyDown={(event) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        const rtl = document.documentElement.getAttribute("dir") === "rtl";
        const step = (event.key === "ArrowRight") !== rtl ? 1 : -1;
        const index = tabs.findIndex((item) => item.id === tab);
        const next = tabs[(index + step + tabs.length) % tabs.length];
        if (!next) return;
        onTabChange(next.id);
        event.currentTarget
          .querySelector<HTMLButtonElement>(`[data-sidebar-tab="${next.id}"]`)
          ?.focus();
      }}
    >
      {tabs.map((item) => {
        const selected = item.id === tab;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            data-sidebar-tab={item.id}
            onClick={() => onTabChange(item.id)}
            className={`min-w-0 flex-1 truncate rounded-md px-2 py-1 text-[13px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none ${
              selected
                ? "bg-background font-medium text-foreground shadow-xs"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

function NavRow({
  icon,
  label,
  hint,
  onClick,
  testId,
}: {
  icon: ReactNode;
  label: string;
  hint?: ReactNode;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="flex h-8 w-full items-center gap-2.5 rounded-lg px-2.5 text-start text-[14px] text-foreground/85 outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="flex w-4 justify-center text-muted-foreground">{icon}</span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {hint}
    </button>
  );
}

/**
 * First rows of the chat tab: new chat, search (Ctrl/⌘+K), the artifacts gallery and the review
 * inbox with its count of open cards (RAK-426).
 */
export function SidebarNav({
  onNewChat,
  onSearch,
  onOpenPage,
}: {
  onNewChat: () => void;
  onSearch: () => void;
  /** Opens a page of the app such as /app/artifacts. */
  onOpenPage: (path: string) => void;
}) {
  const { t } = useLingui();
  const reviewCount = useReviewCount();
  const mod = isApplePlatform() ? "⌘" : t`Ctrl`;
  const iconProps = { size: 15, strokeWidth: 1.8, "aria-hidden": true } as const;
  return (
    <nav aria-label={t`Shortcuts`} className="mb-2 flex flex-col gap-0.5">
      <NavRow
        testId="sidebar-new-chat"
        icon={<Plus {...iconProps} />}
        label={t`New chat`}
        onClick={onNewChat}
      />
      <NavRow
        testId="sidebar-open-search"
        icon={<Search {...iconProps} />}
        label={t`Search`}
        hint={
          <Kbd className="text-[11px]" aria-label={t`Shortcut ${mod} K`}>
            {mod} K
          </Kbd>
        }
        onClick={onSearch}
      />
      <NavRow
        testId="sidebar-artifacts"
        icon={<LayoutGrid {...iconProps} />}
        label={t`Artifacts`}
        onClick={() => onOpenPage("/app/artifacts")}
      />
      {reviewInboxEnabled() ? (
        <NavRow
          testId="sidebar-review"
          icon={<Inbox {...iconProps} />}
          label={t`Review`}
          hint={
            reviewCount ? (
              <span
                data-testid="sidebar-review-badge"
                className="min-w-5 rounded-full bg-primary px-1.5 text-center text-[11px] font-semibold leading-5 text-primary-foreground tabular-nums"
              >
                <span aria-hidden="true">{reviewCount > 99 ? "99+" : reviewCount}</span>
                <span className="sr-only">{t`${reviewCount} open`}</span>
              </span>
            ) : null
          }
          onClick={() => onOpenPage("/app/review")}
        />
      ) : null}
    </nav>
  );
}

/**
 * Routines of the whole space (routines.listSpace) with their bot and next run. The chat tab shows
 * the first few with a link to the routines tab; a click opens the routine editor.
 */
export function SidebarRoutines({
  routines,
  bots,
  limit,
  onOpen,
  onShowAll,
}: {
  routines: readonly Routine[];
  bots: readonly Pick<Bot, "id" | "name">[];
  limit?: number;
  onOpen: (routine: Routine) => void;
  onShowAll?: () => void;
}) {
  const { t } = useLingui();
  const names = new Map(bots.map((bot) => [bot.id, bot.name]));
  const shown = limit === undefined ? routines : routines.slice(0, limit);
  return (
    <section
      aria-labelledby="sidebar-routines-heading"
      className="mt-3"
      data-testid="sidebar-routines"
    >
      <div className="flex items-center justify-between px-2.5 py-1">
        <h2
          id="sidebar-routines-heading"
          className="text-[11px] font-semibold uppercase tracking-wider text-foreground"
        >
          <Trans>Routines</Trans>
        </h2>
        {onShowAll && routines.length > 0 ? (
          <button
            type="button"
            onClick={onShowAll}
            className="rounded text-[12px] text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Trans>All</Trans>
          </button>
        ) : null}
      </div>
      {routines.length === 0 ? (
        <p className="px-2.5 py-1 text-[13px] text-muted-foreground">
          <Trans>No routines yet</Trans>
        </p>
      ) : (
        <ul className="flex flex-col gap-0.5">
          {shown.map((routine) => {
            const next = routine.active ? formatNextRun(routine.nextRunAt) : null;
            const status = routine.active ? (next ?? t`no next run`) : t`paused`;
            return (
              <li key={routine.id}>
                <button
                  type="button"
                  data-testid="sidebar-routine"
                  onClick={() => onOpen(routine)}
                  className="flex w-full items-start gap-2.5 rounded-lg px-2.5 py-1.5 text-start outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Clock
                    size={14}
                    strokeWidth={1.8}
                    aria-hidden="true"
                    className="mt-1 shrink-0 text-muted-foreground"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] text-foreground/90" dir="auto">
                      {routine.name}
                    </span>
                    <span className="block truncate text-[12px] text-muted-foreground" dir="auto">
                      {names.get(routine.botId) ?? ""} · {status}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
